import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const Medicines = ({ screenAction, triggerSave }) => {
  const initialFormState = {
    medicine_id: "",
    medicine_name: "",
    category: "",
    drug_type: "",
    composition: "",
    manufacturer: "",
    supplier: "",
    purchase_price: "",
    mrp: "",
    selling_price: "",
    gst_percentage: "",
    min_level: "",
    max_level: "",
    reorder_level: "",
    storage_condition: "",
    barcode: "",
    status: "Active"
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [medicines, setMedicines] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]); // To hold data from Supplier Master

  // 🔥 Fetch Next ID
  const fetchNextId = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/medicine/id?employee_id=${user.id}`);
      const data = await res.json();
      setFormData(prev => ({ ...prev, medicine_id: data.medicine_id }));
    } catch (err) {
      console.error("Failed to fetch next ID", err);
    }
  };

  // 🔥 Fetch Suppliers for Dropdown
  const fetchSuppliers = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      // Adjust this URL if your supplier api is different
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/suppliers?employee_id=${user.id}`);
      if(res.ok) {
        const data = await res.json();
        setSuppliersList(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch suppliers");
    }
  };

  useEffect(() => {
    fetchNextId();
    fetchSuppliers();
  }, []);

  const clearForm = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/medicine/id?employee_id=${user.id}`);
    const data = await res.json();

    setFormData({
      ...initialFormState,
      medicine_id: data.medicine_id
    });
    setSearchText("");
    setMedicines([]);
  };

  // 🔍 Search
  const searchMedicines = async (text) => {
    setSearchText(text);
    if (text.length < 2) {
      setMedicines([]);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/medicines/search?q=${text}&employee_id=${user.id}`);
      const data = await res.json();
      setMedicines(data);
      setShowDropdown(true);
    } catch (err) {
      console.error(err);
    }
  };

  const selectMedicine = (item) => {
    setFormData(item);
    setSearchText(`${item.medicine_id} | ${item.medicine_name}`);
    setShowDropdown(false);

    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // 🔄 Mode control
  useEffect(() => {
    const init = async () => {
      if (screenAction === "add") {
        clearForm();
        await fetchNextId();
        setIsDisabled(false);
      }
      if (screenAction === "edit") {
        clearForm();
        await fetchNextId();
        setIsDisabled(false);
      }
      if (screenAction === "view") {
        clearForm();
        setIsDisabled(true);
      }
      if (screenAction === "print") {
        printForm();
      }
    };
    init();
  }, [screenAction]);

  // 💾 Save
  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") {
      handleSubmit();
    }
  }, [triggerSave]);

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    
    if(!formData.medicine_name) return alert("Medicine name is required.");

    const payload = { ...formData, created_by: user.id };
    delete payload.created_at;
    delete payload.updated_at;

    const method = formData.created_at ? "PUT" : "POST";
    const url = method === "POST"
        ? "https://hmsapi.emedhatechnologies.com/master/api/api/medicine"
        : `https://hmsapi.emedhatechnologies.com/master/api/api/medicine/${formData.medicine_id}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(`Medicine ${method === "POST" ? "Added" : "Updated"} Successfully`);
        if (method === "POST") clearForm();
      } else {
        alert("Error saving medicine");
      }
    } catch (err) {
      alert("Network error");
    }
  };

  // 🖨️ Print Form
  const printForm = () => {
    if (!formData.medicine_name) return alert("Select or create a medicine to print.");

    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Medicine Details - ${formData.medicine_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #282f81; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #11abea; font-size: 18px; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px; }
            .info-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 160px; color: #555; }
            .value { flex: 1; font-weight: 500; color: #000; }
            .price-tag { color: #27ae60; font-weight: bold; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>123 Medical Park, Health City - 560001</p>
            </div>
            <div class="report-title">
              <h2>MEDICINE MASTER RECORD</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Basic Information</div>
          <div class="info-grid">
            <div class="info-item"><span class="label">Medicine ID:</span> <span class="value">${formData.medicine_id}</span></div>
            <div class="info-item"><span class="label">Name / Brand:</span> <span class="value">${formData.medicine_name}</span></div>
            <div class="info-item"><span class="label">Category:</span> <span class="value">${formData.category}</span></div>
            <div class="info-item"><span class="label">Drug Type:</span> <span class="value">${formData.drug_type}</span></div>
            <div class="info-item"><span class="label">Composition:</span> <span class="value">${formData.composition || '-'}</span></div>
            <div class="info-item"><span class="label">Barcode:</span> <span class="value">${formData.barcode || '-'}</span></div>
          </div>

          <div class="section-header">Supply & Storage</div>
          <div class="info-grid">
            <div class="info-item"><span class="label">Manufacturer:</span> <span class="value">${formData.manufacturer || '-'}</span></div>
            <div class="info-item"><span class="label">Supplier:</span> <span class="value">${formData.supplier || '-'}</span></div>
            <div class="info-item"><span class="label">Storage Condition:</span> <span class="value">${formData.storage_condition}</span></div>
            <div class="info-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
          </div>

          <div class="section-header">Pricing & Inventory Levels</div>
          <div class="info-grid">
            <div class="info-item"><span class="label">Purchase Price:</span> <span class="value price-tag">₹${formData.purchase_price || '0.00'}</span></div>
            <div class="info-item"><span class="label">MRP:</span> <span class="value price-tag">₹${formData.mrp || '0.00'}</span></div>
            <div class="info-item"><span class="label">Selling Price:</span> <span class="value price-tag">₹${formData.selling_price || '0.00'}</span></div>
            <div class="info-item"><span class="label">GST %:</span> <span class="value">${formData.gst_percentage || '0'}%</span></div>
            <div class="info-item"><span class="label">Min Level:</span> <span class="value">${formData.min_level || '0'}</span></div>
            <div class="info-item"><span class="label">Max Level:</span> <span class="value">${formData.max_level || '0'}</span></div>
            <div class="info-item"><span class="label">Reorder Level:</span> <span class="value">${formData.reorder_level || '0'}</span></div>
          </div>

          <div class="footer">
            This is a system-generated record from the Pharmacy Management Module.
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();

    setTimeout(() => {
      newWindow.print();
      newWindow.close();
    }, 500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="registration-container fade-in">

      {/* SEARCH BAR */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Medicine</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by Medicine ID, Name, or Barcode"
            onChange={(e) => searchMedicines(e.target.value)}
          />
          {showDropdown && medicines.length > 0 && (
            <div className="patient-dropdown">
              {medicines.map((m) => (
                <div key={m.medicine_id} className="patient-item" onClick={() => selectMedicine(m)}>
                  {m.medicine_id} | {m.medicine_name} | {m.drug_type}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        
        {/* BASIC INFORMATION */}
        <div className="form-section-header">
          <h3>Basic Information</h3>
        </div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Medicine ID</label>
            <input type="text" name="medicine_id" value={formData.medicine_id || ""} disabled />
          </div>

          <div className="input-box">
            <label>Medicine Name (Generic + Brand) <span style={{color:'red'}}>*</span></label>
            <input type="text" name="medicine_name" value={formData.medicine_name || ""} disabled={isDisabled} onChange={handleChange} placeholder="e.g. Paracetamol (Crocin)" />
          </div>

          <div className="input-box">
            <label>Category</label>
            <select name="category" value={formData.category || ""} disabled={isDisabled} onChange={handleChange}>
              <option value="">Select Category</option>
              <option value="Tablet">Tablet</option>
              <option value="Capsule">Capsule</option>
              <option value="Syrup">Syrup</option>
              <option value="Injection">Injection</option>
              <option value="Ointment">Ointment</option>
              <option value="Drops">Drops (Eye/Ear)</option>
              <option value="Inhaler">Inhaler</option>
            </select>
          </div>

          <div className="input-box">
            <label>Drug Type</label>
            <select name="drug_type" value={formData.drug_type || ""} disabled={isDisabled} onChange={handleChange}>
              <option value="">Select Type</option>
              <option value="OTC">OTC (Over The Counter)</option>
              <option value="Prescription">Prescription</option>
              <option value="Narcotic">Narcotic</option>
            </select>
          </div>

          <div className="input-box" style={{ gridColumn: 'span 2' }}>
            <label>Composition</label>
            <input type="text" name="composition" value={formData.composition || ""} disabled={isDisabled} onChange={handleChange} placeholder="e.g. Paracetamol 500mg" />
          </div>
        </div>

        {/* SUPPLY & STORAGE */}
        <div className="form-section-header">
          <h3>Supply & Storage</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Manufacturer Name</label>
            <input type="text" name="manufacturer" value={formData.manufacturer || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>Supplier</label>
            <select name="supplier" value={formData.supplier || ""} disabled={isDisabled} onChange={handleChange}>
              <option value="">Select Supplier</option>
              {suppliersList.map((sup, idx) => (
                <option key={idx} value={sup.Supp_Nm}>{sup.Supp_Nm}</option>
              ))}
            </select>
          </div>

          <div className="input-box">
            <label>Storage Condition</label>
            <select name="storage_condition" value={formData.storage_condition || ""} disabled={isDisabled} onChange={handleChange}>
              <option value="">Select Condition</option>
              <option value="Room Temperature">Room Temp (15-25°C)</option>
              <option value="Refrigerated">Refrigerated (2-8°C)</option>
              <option value="Cool Place">Cool Place (8-15°C)</option>
            </select>
          </div>

          <div className="input-box">
            <label>Barcode</label>
            <input type="text" name="barcode" value={formData.barcode || ""} disabled={isDisabled} onChange={handleChange} placeholder="Scan or Type" />
          </div>
        </div>

        {/* PRICING & INVENTORY */}
        <div className="form-section-header">
          <h3>Pricing & Inventory Levels</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Purchase Price (₹)</label>
            <input type="number" name="purchase_price" value={formData.purchase_price || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>MRP (₹)</label>
            <input type="number" name="mrp" value={formData.mrp || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>Selling Price (₹)</label>
            <input type="number" name="selling_price" value={formData.selling_price || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>GST (%)</label>
            <input type="number" name="gst_percentage" value={formData.gst_percentage || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>Min Level</label>
            <input type="number" name="min_level" value={formData.min_level || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>Max Level</label>
            <input type="number" name="max_level" value={formData.max_level || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>Reorder Level</label>
            <input type="number" name="reorder_level" value={formData.reorder_level || ""} disabled={isDisabled} onChange={handleChange} />
          </div>

          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status || "Active"} disabled={isDisabled} onChange={handleChange}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

      </form>
    </div>
  );
};

export default Medicines;